import { useEffect, useRef } from "react";

interface MiniModeArgs {
  radius: number;
}

export const useMiniMode = (args: MiniModeArgs) => {
  const { radius } = args;
  const circleRef = useRef<SVGCircleElement | null>(null);
  const circumference = radius * 2 * Math.PI;

  useEffect(() => {
    if (circleRef.current) {
      /**
       * this is a trick to animate an svg circle
       * when stroke dash-offset equals the circumference, the circle appears "empty"
       * when stroke dash-offset equals 0, it appears "full"
       */
      circleRef.current.style.strokeDasharray = `${circumference} ${circumference}`;
      circleRef.current.style.strokeDashoffset = `${circumference}`;
    }
  }, []);

  const onPlayMini = (duration: number, currentTime: number) => {
    if (circleRef.current) {
      const timeLeft = duration - currentTime;
      circleRef.current.style.opacity = "1";

      if (timeLeft === duration) {
        /**
         * set the progress circle back to "empty" before replaying the audio from the beginning
         * window.getComputedStyle forces a restyle + enables us to set the stroke dash-offset
         * twice here, once to "empty" and again to "full" with the transtion duration set as the
         * duration of audio
         * https://stackoverflow.com/questions/18564942/clean-way-to-programmatically-use-css-transitions-from-js/21283499#21283499
         */
        circleRef.current.style.strokeDashoffset = `${circumference}`;
        window.getComputedStyle(circleRef.current).strokeDashoffset;
      }

      circleRef.current.style.transition = `stroke-dashoffset ${timeLeft}s linear`;
      circleRef.current.style.strokeDashoffset = "0";
    }
  };

  const onPauseMini = (duration: number, currentTime: number) => {
    if (circleRef.current) {
      const computedStyle = window.getComputedStyle(circleRef.current);
      /**
       * pause event fires at the end of the track as well
       * only update the offset if the user clicks pause mid-way through
       */
      if (duration !== currentTime) {
        circleRef.current.style.strokeDashoffset =
          computedStyle.strokeDashoffset;
        circleRef.current.style.transition = "none";
      }
    }
  };

  const onEndMini = () => {
    if (circleRef.current) {
      circleRef.current.style.transition = "opacity 0.4s ease-out 0.6s";
      circleRef.current.style.opacity = "0";
    }
  };

  return { circleRef, onPlayMini, onPauseMini, onEndMini };
};
